// src/components/chatbot/MessageFeedback.tsx
import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSendFeedbackMutation } from '@/services/chatbotApi';

interface MessageFeedbackProps {
  messageId: string;
  businessId: string;
  sourceId?: string;
  sourceType?: string;
  sessionId?: string;
  onFeedbackSent?: (wasHelpful: boolean) => void;
}

const MessageFeedback: React.FC<MessageFeedbackProps> = ({
  messageId,
  businessId,
  sourceId,
  sourceType = 'query-response',
  sessionId,
  onFeedbackSent
}) => {
  const [feedbackSent, setFeedbackSent] = useState<boolean | null>(null);
  const [sendFeedback, { isLoading: isSubmitting }] = useSendFeedbackMutation();

  const handleFeedback = async (wasHelpful: boolean) => {
    if (isSubmitting) return;
    
    try {
      // Use the new flexible endpoint
      await sendFeedback({
        businessId,
        messageId,
        sourceId,
        sourceType: sourceId ? sourceType : 'message',
        sessionId,
        wasHelpful
      });
      
      // Store feedback locally as well for redundancy and analytics
      const feedbackData = {
        messageId,
        timestamp: new Date().toISOString(),
        wasHelpful,
        sourceId,
        sourceType
      };
      
      const feedbackKey = `chatbot-feedback-${businessId}`;
      const existingFeedback = JSON.parse(localStorage.getItem(feedbackKey) || '[]');
      localStorage.setItem(feedbackKey, JSON.stringify([...existingFeedback, feedbackData]));
      
      setFeedbackSent(wasHelpful);
      
      if (onFeedbackSent) {
        onFeedbackSent(wasHelpful);
      }
    } catch (error) {
      console.error('Error sending feedback:', error);
      // Still mark as sent for user experience
      setFeedbackSent(wasHelpful);
    }
  };

  if (feedbackSent !== null) {
    return (
      <div className="text-xs text-muted-foreground">
        {feedbackSent 
          ? 'Thanks for your feedback!' 
          : 'Thanks for letting us know. We\'ll improve our responses.'}
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-3">
      <span className="text-xs text-muted-foreground">Was this helpful?</span>
      
      <Button 
        variant="ghost" 
        size="sm" 
        className="h-6 w-6 rounded-full p-0" 
        onClick={() => handleFeedback(true)}
        disabled={isSubmitting}
        title="This was helpful"
      >
        <ThumbsUp className="h-3.5 w-3.5 text-muted-foreground hover:text-green-500" />
        <span className="sr-only">Helpful</span>
      </Button>
      
      <Button 
        variant="ghost" 
        size="sm" 
        className="h-6 w-6 rounded-full p-0" 
        onClick={() => handleFeedback(false)}
        disabled={isSubmitting}
        title="This was not helpful"
      >
        <ThumbsDown className="h-3.5 w-3.5 text-muted-foreground hover:text-red-500" />
        <span className="sr-only">Not helpful</span>
      </Button>
    </div>
  );
};

export default MessageFeedback;